import { useRef, useState, useCallback } from 'react';
import SceneLayers from './SceneLayers';
import AssetLibrary from '../sidebar/AssetLibrary';
import type { Asset } from '@spine-workspace/shared';

const MIN_PCT = 20;
const MAX_PCT = 80;

// Header height in px — matches `py-3` (12px × 2) + ~20px text = 44px
const HEADER_H = 44;

interface LeftPanelProps {
  onDoubleClickAsset?: (asset: Asset) => void;
  viewOnly?: boolean;
}

export default function LeftPanel({ onDoubleClickAsset, viewOnly }: LeftPanelProps) {
  const [topPct, setTopPct] = useState(65);
  const [topCollapsed, setTopCollapsed] = useState(false);
  const [bottomCollapsed, setBottomCollapsed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const startResize = useCallback(
    (e: React.MouseEvent) => {
      if (topCollapsed || bottomCollapsed) return;
      e.preventDefault();
      const startY = e.clientY;
      const startPct = topPct;

      const onMove = (ev: MouseEvent) => {
        const container = containerRef.current;
        if (!container) return;
        const totalH = container.clientHeight;
        const deltaPct = ((ev.clientY - startY) / totalH) * 100;
        setTopPct(Math.max(MIN_PCT, Math.min(MAX_PCT, startPct + deltaPct)));
      };

      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [topPct, topCollapsed, bottomCollapsed]
  );

  // Height calculation
  const HANDLE_H = 5;
  const topHeight = topCollapsed
    ? HEADER_H
    : bottomCollapsed
    ? `calc(100% - ${HEADER_H}px)`
    : `${topPct}%`;

  const bottomHeight = bottomCollapsed
    ? HEADER_H
    : topCollapsed
    ? `calc(100% - ${HEADER_H}px)`
    : `${100 - topPct}%`;

  const showHandle = !topCollapsed && !bottomCollapsed;

  return (
    <div ref={containerRef} className="flex flex-col h-full overflow-hidden">
      {/* Top — Hierarchy */}
      <div
        style={{
          height: typeof topHeight === 'number' ? `${topHeight}px` : topHeight,
          transition: 'height 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
        className="overflow-hidden flex flex-col"
        data-testid="hierarchy-section"
      >
        <SceneLayers
          isCollapsed={topCollapsed}
          onToggleCollapse={() => setTopCollapsed((v) => !v)}
          viewOnly={viewOnly}
        />
      </div>

      {/* Resize handle — only when both sections are expanded */}
      {showHandle && (
        <div
          onMouseDown={startResize}
          className="h-[5px] shrink-0 cursor-row-resize group relative hover:bg-accent/20 active:bg-accent/40 transition-colors duration-fast border-t border-border-subtle select-none"
          style={{ height: HANDLE_H }}
        >
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px bg-border-subtle group-hover:bg-accent/40 transition-colors" />
        </div>
      )}

      {/* Bottom — Assets */}
      <div
        style={{
          height: typeof bottomHeight === 'number' ? `${bottomHeight}px` : bottomHeight,
          transition: 'height 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
        className="overflow-hidden flex flex-col min-h-0"
        data-testid="assets-section"
      >
        <AssetLibrary
          onDoubleClickAsset={onDoubleClickAsset}
          isCollapsed={bottomCollapsed}
          onToggleCollapse={() => setBottomCollapsed((v) => !v)}
          viewOnly={viewOnly}
        />
      </div>
    </div>
  );
}
